import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import clsx from 'clsx';

const COLUMNS = [
  { id: 'pending',   label: 'Pending Review',   tone: 'bg-amber-50 border-amber-200' },
  { id: 'approved',  label: 'Queue (Approved)', tone: 'bg-emerald-50 border-emerald-200' },
  { id: 'completed', label: 'Completed',         tone: 'bg-blue-50 border-blue-200' },
];

const CATEGORIES = [
  { id: 'collaboration',     label: 'Collaboration' },
  { id: 'follow-through',    label: 'Follow-through' },
  { id: 'crm-general',       label: 'CRM general' },
  { id: 'trailer-specific',  label: 'Trailer-specific' },
  { id: 'general',           label: 'General' },
];

const CATEGORY_TONE = {
  'collaboration':     'bg-blue-100 text-blue-900',
  'follow-through':    'bg-purple-100 text-purple-900',
  'crm-general':       'bg-slate-100 text-slate-900',
  'trailer-specific':  'bg-amber-100 text-amber-900',
  'general':           'bg-gray-100 text-gray-900',
};

function fmtDate(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString();
}

export default function ChangeRequests() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [showDenied, setShowDenied] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [editing, setEditing] = useState(null); // request object or 'new'

  const isAdmin = user && user.role !== 'sales';

  const list = useQuery({
    queryKey: ['change-requests', { showDenied }],
    queryFn: () => api.get(`/change-requests${showDenied ? '?include_denied=true' : ''}`).then(r => r.data.data),
  });

  const update = useMutation({
    mutationFn: ({ id, ...body }) => api.patch(`/change-requests/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['change-requests'] }),
  });

  const all = list.data || [];
  const filtered = filterCategory === 'all' ? all : all.filter(r => r.category === filterCategory);
  const byStatus = {
    pending:   filtered.filter(r => r.status === 'pending'),
    approved:  filtered.filter(r => r.status === 'approved'),
    completed: filtered.filter(r => r.status === 'completed'),
    denied:    filtered.filter(r => r.status === 'denied'),
  };

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="section-eyebrow">Backlog</div>
          <h1 className="text-2xl">Change requests</h1>
          <div className="rule-crimson" />
        </div>
        <button className="btn-primary" onClick={() => setEditing('new')}>+ New request</button>
      </header>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted">Category:</span>
        <button onClick={() => setFilterCategory('all')}
          className={clsx('px-2 py-1 rounded',
            filterCategory === 'all' ? 'bg-navy text-white' : 'bg-panel text-navy')}>
          All
        </button>
        {CATEGORIES.map(c => (
          <button key={c.id} onClick={() => setFilterCategory(c.id)}
            className={clsx('px-2 py-1 rounded',
              filterCategory === c.id ? 'bg-navy text-white' : 'bg-panel text-navy')}>
            {c.label}
          </button>
        ))}
        <label className="ml-auto inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={showDenied} onChange={e => setShowDenied(e.target.checked)} />
          Show denied ({byStatus.denied.length})
        </label>
      </div>

      <div className={clsx('grid gap-3 grid-cols-1', showDenied ? 'md:grid-cols-4' : 'md:grid-cols-3')}>
        {COLUMNS.map(col => (
          <Column key={col.id} col={col} items={byStatus[col.id]}
                  isAdmin={isAdmin}
                  onEdit={setEditing}
                  onMove={(id, status) => update.mutate({ id, status })} />
        ))}
        {showDenied && (
          <Column col={{ id: 'denied', label: 'Denied', tone: 'bg-rose-50 border-rose-200' }}
                  items={byStatus.denied}
                  isAdmin={isAdmin}
                  onEdit={setEditing}
                  onMove={(id, status) => update.mutate({ id, status })} />
        )}
      </div>

      {editing && (
        <RequestEditor
          request={editing === 'new' ? null : editing}
          isAdmin={isAdmin}
          userEmail={user?.email}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['change-requests'] });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function Column({ col, items, isAdmin, onEdit, onMove }) {
  return (
    <div className={clsx('rounded border p-2', col.tone)}>
      <div className="flex items-baseline justify-between mb-2 px-1">
        <div className="font-semibold text-navy text-sm">{col.label}</div>
        <div className="text-xs text-muted">{items.length}</div>
      </div>
      <div className="space-y-2">
        {items.length === 0 && (
          <div className="text-xs text-muted text-center py-3">Empty</div>
        )}
        {items.map(r => (
          <Card key={r.id} request={r} isAdmin={isAdmin}
                onEdit={() => onEdit(r)} onMove={onMove} />
        ))}
      </div>
    </div>
  );
}

function Card({ request: r, isAdmin, onEdit, onMove }) {
  return (
    <div className="bg-white rounded p-3 border border-panelDark shadow-sm">
      <button onClick={onEdit} className="text-left w-full">
        <div className="flex items-start justify-between gap-2">
          <div className="font-medium text-navy text-sm flex-1">{r.title}</div>
          <span className={clsx('text-[10px] px-2 py-0.5 rounded uppercase tracking-wide', CATEGORY_TONE[r.category] || CATEGORY_TONE.general)}>
            {r.category}
          </span>
        </div>
        {r.description && (
          <div className="text-xs text-muted mt-1 line-clamp-3">{r.description}</div>
        )}
        <div className="text-[11px] text-muted mt-2">
          Submitted by <span className="text-navy">{r.submitted_by_name || r.submitted_by_email || 'unknown'}</span> · {fmtDate(r.created_at)}
        </div>
        {r.review_notes && (
          <div className="text-[11px] mt-1 italic text-navy/80">"{r.review_notes}"</div>
        )}
        {r.completion_notes && (
          <div className="text-[11px] mt-1 italic text-navy/80">"{r.completion_notes}"</div>
        )}
      </button>

      {isAdmin && (
        <div className="mt-2 pt-2 border-t border-panelDark">
          <div className="flex gap-1 flex-wrap">
            {r.status !== 'approved' && r.status !== 'completed' && (
              <button className="text-xs px-2 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                      onClick={() => onMove(r.id, 'approved')}>
                ✓ Approve
              </button>
            )}
            {r.status === 'approved' && (
              <button className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                      onClick={() => onMove(r.id, 'completed')}>
                ✓✓ Mark complete
              </button>
            )}
            {r.status === 'completed' && (
              <button className="text-xs px-2 py-1 rounded bg-panel text-navy hover:bg-panelDark"
                      onClick={() => onMove(r.id, 'approved')}>
                ↺ Reopen
              </button>
            )}
            {r.status !== 'denied' && r.status !== 'completed' && (
              <button className="text-xs px-2 py-1 rounded bg-rose-600 text-white hover:bg-rose-700"
                      onClick={() => onMove(r.id, 'denied')}>
                ✗ Deny
              </button>
            )}
            {r.status !== 'pending' && (
              <button className="text-xs px-2 py-1 rounded bg-panel text-navy hover:bg-panelDark"
                      onClick={() => onMove(r.id, 'pending')}>
                ↺ Reset
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function RequestEditor({ request, isAdmin, userEmail, onClose, onSaved }) {
  const isNew = !request;
  const canEditContent = isNew || isAdmin || (request && request.submitted_by_email === userEmail);

  const [form, setForm] = useState(request ? {
    title:            request.title || '',
    description:      request.description || '',
    category:         request.category || 'general',
    review_notes:     request.review_notes || '',
    completion_notes: request.completion_notes || '',
  } : {
    title: '', description: '', category: 'general', review_notes: '', completion_notes: '',
  });

  const save = useMutation({
    mutationFn: () => {
      if (isNew) {
        return api.post('/change-requests', {
          title:       form.title,
          description: form.description,
          category:    form.category,
        });
      }
      const body = {};
      if (canEditContent) {
        body.title       = form.title;
        body.description = form.description;
        body.category    = form.category;
      }
      if (isAdmin) {
        body.review_notes     = form.review_notes;
        body.completion_notes = form.completion_notes;
      }
      return api.patch(`/change-requests/${request.id}`, body);
    },
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 z-50 bg-navy/50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 space-y-3 shadow-panel max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold">{isNew ? 'New change request' : 'Change request'}</h3>

        {!isNew && (
          <div className="text-xs text-muted">
            Submitted by <span className="text-navy">{request.submitted_by_name || request.submitted_by_email}</span> · {fmtDate(request.created_at)}
            {' · status: '}<span className="text-navy capitalize">{request.status}</span>
          </div>
        )}

        <div>
          <label className="label">Title *</label>
          <input className="input" value={form.title}
                 disabled={!canEditContent}
                 onChange={e => setForm({ ...form, title: e.target.value })} autoFocus={isNew} />
        </div>

        <div>
          <label className="label">Category</label>
          <select className="input" value={form.category}
                  disabled={!canEditContent}
                  onChange={e => setForm({ ...form, category: e.target.value })}>
            {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>

        <div>
          <label className="label">Description</label>
          <textarea className="input min-h-[120px]" value={form.description}
                    disabled={!canEditContent}
                    onChange={e => setForm({ ...form, description: e.target.value })} />
        </div>

        {isAdmin && !isNew && (
          <div>
            <label className="label">Review notes</label>
            <textarea className="input min-h-[60px]" value={form.review_notes}
                      placeholder="Why approved/denied, scope notes, etc."
                      onChange={e => setForm({ ...form, review_notes: e.target.value })} />
          </div>
        )}

        {isAdmin && !isNew && (request.status === 'approved' || request.status === 'completed') && (
          <div>
            <label className="label">Completion notes</label>
            <textarea className="input min-h-[60px]" value={form.completion_notes}
                      placeholder="What was shipped, links to PR/commit, follow-ups, etc."
                      onChange={e => setForm({ ...form, completion_notes: e.target.value })} />
          </div>
        )}

        {save.isError && (
          <div className="text-crimson text-sm">{save.error?.response?.data?.error || 'Save failed'}</div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={!form.title || save.isPending}
                  onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
